import { isValidEmail } from 'src/utils/device';

/**
 * Client-side logic for the "change account email" card (mirror of the sales cabinet card).
 * Kept pure and separate from the component so it can be unit-tested in the node env — the
 * app has no component-test setup (only vitest specs for pure helpers, e.g. purchaseGate).
 *
 * Copy for a refusal that came back from api lives in `securityError.ts`, which covers the whole
 * hardened surface (both codes, the wallet signature, the attempt counter), not just this card.
 */

export type NewEmailCheck =
  | { ok: true; email: string }
  | { ok: false; reason: 'empty' | 'invalid' | 'unchanged' };

/**
 * Guard for the new-email input before we spend an OTP round-trip: trims, rejects an invalid
 * address, and rejects a no-op change to the current address (case-insensitive). The server
 * still re-checks validity and uniqueness — this only gives instant feedback and avoids a
 * pointless code email to the same inbox.
 */
export const checkNewEmail = (currentEmail: string, input: string): NewEmailCheck => {
  const email = input.trim();
  if (!email) return { ok: false, reason: 'empty' };
  if (!isValidEmail(email)) return { ok: false, reason: 'invalid' };
  if (email.toLowerCase() === currentEmail.trim().toLowerCase()) {
    return { ok: false, reason: 'unchanged' };
  }
  return { ok: true, email };
};

type NewEmailFailReason = Extract<NewEmailCheck, { ok: false }>['reason'];

const NEW_EMAIL_ERROR_COPY: Record<NewEmailFailReason, string> = {
  empty: 'Enter your new email address.',
  invalid: 'Enter a valid email address.',
  unchanged: 'That is already your account email.',
};

/** Buyer-facing copy for a failed new-email guard. */
export const newEmailErrorCopy = (reason: NewEmailFailReason): string =>
  NEW_EMAIL_ERROR_COPY[reason];
